if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        // Try multiple selectors to find the quantity input
        this.input = this.querySelector('.product-form__buttons .quantity__input') ||
                     this.querySelector('quantity-input .quantity__input') ||
                     this.querySelector('.quantity__input');

        this.currentVariant = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('[type="submit"]');

        // Get the variant price from the data attribute (already in cents)
        this.baseVariantPrice = parseInt(this.dataset.variantPrice) || 0;
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      quantityChangeUnsubscriber = undefined;

      connectedCallback() {
        if (!this.input) {
          return;
        }

        // quantityForm is not required for our functionality, so we'll continue without it
        this.quantityForm = this.querySelector('.product-form__quantity');

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
          if (event.data.sectionId !== sectionId) return;
          this.updateQuantityRules(event.data.sectionId, event.data.html);
          this.setQuantityBoundries();
        });

        // Listen for quantity changes from the product form's quantity input
        this.setupQuantityChangeListener();
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.quantityChangeUnsubscriber) {
          this.quantityChangeUnsubscriber();
        }
      }

      setupQuantityChangeListener() {
        // Find the quantity input in the product form buttons
        const productFormQuantityInput = this.querySelector('.product-form__buttons quantity-input');

        if (productFormQuantityInput) {
          const quantityInput = productFormQuantityInput.querySelector('input');

          if (quantityInput) {
            // Listen for the custom change event emitted by the QuantityInput component
            const handleQuantityChange = (event) => {
              this.updateProductFormPrice();
            };

            // Listen for the change event that bubbles from quantity-input
            productFormQuantityInput.addEventListener('change', handleQuantityChange);

            // Also try listening directly on the input element as a fallback
            quantityInput.addEventListener('change', (event) => {
              handleQuantityChange(event);
            });

            // Store the cleanup function
            this.quantityChangeUnsubscriber = () => {
              productFormQuantityInput.removeEventListener('change', handleQuantityChange);
              quantityInput.removeEventListener('change', handleQuantityChange);
            };
          }
        }
      }

      getProductFormPrice() {
        const priceElement = this.querySelector('.product-form__price');
        if (priceElement && priceElement.textContent.trim()) {
          try {
            // Extract the price text and parse it to get the numeric value
            const priceText = priceElement.textContent.trim();
            // Remove currency symbols and commas, then parse
            const numericPrice = parseFloat(priceText.replace(/[^0-9.-]/g, ''));
            if (!isNaN(numericPrice)) {
              return numericPrice * 100; // Convert to cents for calculation
            }
          } catch (error) {
            console.warn('Error parsing product form price:', error);
          }
        }
        return 0;
      }

      getCurrentQuantity() {
        const productFormQuantityInput = this.querySelector('.product-form__buttons quantity-input input');
        
        if (productFormQuantityInput) {
          const quantity = parseInt(productFormQuantityInput.value) || 1;
          return quantity;
        }
        
        return 1;
      }

      getBaseVariantPrice() {
        return this.baseVariantPrice;
      }

      getVariantDataFromPage(variantId) {
        // Try to find variant data in the page's JSON
        try {
          const productDataElement = document.querySelector('[data-product-json]');
          if (productDataElement) {
            const productData = JSON.parse(productDataElement.textContent);
            return productData.variants.find(variant => variant.id.toString() === variantId.toString());
          }
        } catch (error) {
          console.warn('Error getting variant data from page:', error);
        }
        return null;
      }

      formatPrice(priceInCents) {
        try {
          // Format as $8,000 (no cents)
          const dollars = Math.round(priceInCents / 100);
          return '$' + dollars.toLocaleString();
        } catch (error) {
          console.warn('Error formatting price:', error);
          return '$0';
        }
      }

      updateProductFormPrice() {
        const priceElement = this.querySelector('.product-form__price');

        if (!priceElement) {
          return;
        }

        try {
          const baseVariantPrice = this.getBaseVariantPrice();
          const quantity = this.getCurrentQuantity();

          if (baseVariantPrice <= 0 || quantity <= 0) {
            return;
          }

          // Calculate the new total price based on quantity
          let newTotalPrice = baseVariantPrice * quantity;

          // Check if AdditionalItems exists and add its pricing
          const additionalItems = document.querySelector('additional-items');

          if (additionalItems && typeof additionalItems.calculateAdditionalItemsTotal === 'function') {
            const additionalTotal = additionalItems.calculateAdditionalItemsTotal();
            newTotalPrice += additionalTotal;
          }

          const formattedPrice = this.formatPrice(newTotalPrice);

          // Update the price display
          priceElement.textContent = formattedPrice;
        } catch (error) {
          console.error('ProductInfo: Error updating product form price:', error);
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        this.input.min = min;

        if (max) {
          this.input.max = max;
        } else {
          this.input.removeAttribute('max');
        }
        this.input.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;
        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`)
          .then((response) => {
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.setQuantityBoundries();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm?.querySelector(selector);
          const updated = quantityFormUpdated?.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
        
        // Re-setup quantity change listener after variant change
        this.setupQuantityChangeListener();
      }
    }
  );
}
